import type { TiptapThreadStore } from '@blocknote/core/comments';
import { api } from '@/api';
import { DOCUMENT_TYPE } from '@/features/documents/constants';
import type { CollabConfig, CollabSurface } from '@/features/collab/types';

const PREVIEW_MAX = 200;

type InlineNode = {
  type?: string;
  text?: string;
  props?: { userId?: string };
  content?: InlineNode[] | string;
};

type BlockNode = {
  type?: string;
  content?: InlineNode[] | string;
  children?: BlockNode[];
};

const extractMentionUserIds = (body: unknown): string[] => {
  if (!Array.isArray(body)) return [];
  const ids = new Set<string>();
  const walk = (nodes: unknown[]): void => {
    for (const n of nodes) {
      if (!n || typeof n !== 'object') continue;
      const node = n as InlineNode & BlockNode;
      if (node.type === 'mention' && node.props?.userId) {
        ids.add(node.props.userId);
      }
      if (Array.isArray(node.content)) walk(node.content);
      if (Array.isArray(node.children)) walk(node.children);
    }
  };
  walk(body as unknown[]);
  return [...ids];
};

const buildCommentPreview = (body: unknown): string => {
  if (!Array.isArray(body)) return '';
  const parts: string[] = [];
  const walk = (nodes: unknown[]): void => {
    for (const n of nodes) {
      if (!n || typeof n !== 'object') continue;
      const node = n as InlineNode & BlockNode & { props?: { label?: string } };
      if (typeof node.text === 'string') parts.push(node.text);
      else if (node.type === 'mention') {
        const label = node.props?.label || node.props?.userId || '';
        if (label) parts.push(`@${label}`);
      } else if (Array.isArray(node.content)) walk(node.content);
      if (Array.isArray(node.children)) walk(node.children);
    }
  };
  walk(body as unknown[]);
  const text = parts.join(' ').replace(/\s+/g, ' ').trim();
  return text.length > PREVIEW_MAX ? `${text.slice(0, PREVIEW_MAX - 1)}…` : text;
};

export const withMentionNotify = (
  store: TiptapThreadStore,
  ctx: {
    resourceId: string;
    surface: CollabSurface;
    documentId: string;
    buildThreadURL?: (threadId: string) => string;
  },
): TiptapThreadStore => {
  if (ctx.surface === 'artifact') return store;
  const fire = (commentId: string, threadId: string | undefined, body: unknown): void => {
    const taggedUserIds = extractMentionUserIds(body);
    if (taggedUserIds.length === 0) return;
    api
      .post(
        'v1/analysis/collab/comments/notify',
        {
          surface: ctx.surface,
          documentId: ctx.documentId,
          commentId,
          threadId,
          taggedUserIds,
          preview: buildCommentPreview(body),
          targetUrl: threadId ? ctx.buildThreadURL?.(threadId) : undefined,
        },
        { params: { type: DOCUMENT_TYPE.tenderPackage, resourceId: ctx.resourceId } },
      )
      .catch((err) => {
        console.warn('[collab] mention notify failed:', err);
      });
  };

  const origCreateThread = store.createThread.bind(store);
  store.createThread = async (options) => {
    const result = await origCreateThread(options);
    const firstComment = result.comments?.[0];
    if (firstComment) fire(firstComment.id, result.id, options.initialComment.body);
    return result;
  };

  const origAddComment = store.addComment.bind(store);
  store.addComment = async (options) => {
    const result = await origAddComment(options);
    const thread = store.getThread(options.threadId);
    const lastCommentId = thread?.comments?.[thread.comments.length - 1]?.id;
    if (lastCommentId) fire(lastCommentId, options.threadId, options.comment.body);
    return result;
  };

  const origUpdateComment = store.updateComment.bind(store);
  store.updateComment = async (options) => {
    const result = await origUpdateComment(options);
    fire(options.commentId, options.threadId, options.comment.body);
    return result;
  };

  return store;
};

export const flushProvider = (provider: CollabConfig['provider']) =>
  new Promise<void>((resolve, reject) => {
    if (provider.isSynced && !provider.hasUnsyncedChanges) return resolve();
    const synced = () => {
      if (!provider.isSynced || provider.hasUnsyncedChanges) return;
      clearTimeout(timeout);
      provider.off('unsyncedChanges', synced);
      provider.off('synced', synced);
      resolve();
    };
    const timeout = setTimeout(() => {
      provider.off('unsyncedChanges', synced);
      provider.off('synced', synced);
      reject(new Error('Document has unsaved changes'));
    }, 20000);
    provider.on('unsyncedChanges', synced);
    provider.on('synced', synced);
    synced();
  });
